import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Flex, Heading, LoadingOrbit, NumberField, SettingsRow, Switch, TagPicker, Text, type TagOption } from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext.js';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';
import { useCreateTagMutation, useTagsQuery } from '../../../hooks/useTags.js';
import { useEligibilityModel } from '../../../hooks/useEligibilityModel.js';

type OrgWorkflowSettings = {
  autoTagOnPublish: boolean;
  autoTagName: string;
  autoMaintainEnabled: boolean;
  cachePinByDefault: boolean;
  maxParallelExternalTests: number;
};

type WorkflowsSectionProps = {
  isAdmin: boolean;
};

const DEFAULT_TAG_NAME = 'regression suite';

export function WorkflowsSection({ isAdmin }: WorkflowsSectionProps) {
  // Auto-tag fires at different lifecycle moments per model: on approve under the eligibility
  // model (flag ON), on promote under the legacy promotion model (flag OFF). Keep the copy
  // truthful for the org's actual model.
  const eligibilityModel = useEligibilityModel();
  const autoTagMoment = eligibilityModel ? 'approved' : 'promoted';
  const { user } = useAuth();
  // PLG gate (required for new web changes): `canUseSelfServeBilling` is true when the `plg.self_serve_org_creation` knob is on. The cap
  // only means anything for orgs running external suites on Sandboxes v2, so it is hidden
  // everywhere else.
  const showExternalParallelism =
    Boolean(user?.canUseSelfServeBilling) && user?.features?.externalTestSuitesSandboxV2 === true;
  const queryClient = useQueryClient();
  const [settingsDraft, setSettingsDraft] = React.useState<OrgWorkflowSettings | null>(null);
  const [selectedAutoTag, setSelectedAutoTag] = React.useState<TagOption[]>([]);
  const tagsQuery = useTagsQuery();
  const createTagMutation = useCreateTagMutation();

  const settingsQuery = useQuery({
    queryKey: ['org-workflow-settings'],
    queryFn: async (): Promise<{ ok: boolean; data: OrgWorkflowSettings }> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load workflow settings');
      }
      return response.json();
    },
  });

  React.useEffect(() => {
    if (settingsQuery.data?.data) {
      setSettingsDraft(settingsQuery.data.data);
    }
  }, [settingsQuery.data]);

  React.useEffect(() => {
    if (!settingsDraft) return;

    const tagName = settingsDraft.autoTagName.trim();
    if (!tagName) {
      setSelectedAutoTag([]);
      return;
    }

    const tags = (tagsQuery.data?.data ?? []) as TagOption[];
    const existing = tags.find((tag) => tag.name === tagName);
    if (existing) {
      setSelectedAutoTag([existing]);
      return;
    }

    setSelectedAutoTag([{ id: `custom:${tagName}`, name: tagName, color: 'blue' }]);
  }, [settingsDraft?.autoTagName, tagsQuery.data?.data]);

  const updateMutation = useMutation({
    mutationFn: async (payload: Partial<OrgWorkflowSettings>) => {
      const response = await fetch(`${API_URL}/org/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update workflow settings');
      const data = await response.json();
      return data as { ok: boolean; data: OrgWorkflowSettings };
    },
    onSuccess: ({ data }) => {
      setSettingsDraft(data);
      queryClient.invalidateQueries({ queryKey: ['org-workflow-settings'] });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <Card padding="lg">
        <CardContent>
          <Flex align="center" justify="center" className="py-10">
            <LoadingOrbit size="md" />
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card padding="lg">
        <CardContent>
          <Flex direction="column" gap="md" className="py-6">
            <Text className="text-destructive">Failed to load workflow settings.</Text>
            <Flex justify="start">
              <Button variant="outline" onPress={() => settingsQuery.refetch()}>
                Retry
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    );
  }

  if (!settingsDraft) {
    return null;
  }

  const saveSettings = () => {
    const tagName = settingsDraft.autoTagName.trim();
    updateMutation.mutate({
      autoTagOnPublish: settingsDraft.autoTagOnPublish,
      autoTagName: tagName.length > 0 ? tagName : DEFAULT_TAG_NAME,
      autoMaintainEnabled: settingsDraft.autoMaintainEnabled,
      cachePinByDefault: settingsDraft.cachePinByDefault,
      ...(showExternalParallelism
        ? { maxParallelExternalTests: settingsDraft.maxParallelExternalTests }
        : {}),
    });
  };

  return (
    <Card padding="lg">
      <CardHeader>
        <Box>
          <Heading level={3}>Workflow Tagging</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Configure default tagging applied when a flow is {autoTagMoment} for this organization.
          </Text>
        </Box>
      </CardHeader>
      <CardContent>
        <Flex direction="column" gap="md">
          <SettingsRow
            title={`Auto-tag workflows when ${autoTagMoment}`}
            description={`Most teams like to tag flows for certain suites. This will auto-tag a flow when it is ${autoTagMoment}. We suggest "regression suite".`}
            alignAction={settingsDraft.autoTagOnPublish ? 'top' : 'center'}
            onPress={
              isAdmin && !updateMutation.isPending
                ? () =>
                    setSettingsDraft((previous) =>
                      previous
                        ? { ...previous, autoTagOnPublish: !previous.autoTagOnPublish }
                        : previous
                    )
                : undefined
            }
          >
            <Switch
              isDisabled={!isAdmin || updateMutation.isPending}
              isSelected={settingsDraft.autoTagOnPublish}
              onChange={(isSelected) =>
                setSettingsDraft((previous) =>
                  previous ? { ...previous, autoTagOnPublish: isSelected } : previous
                )
              }
            />
          </SettingsRow>

          {settingsDraft.autoTagOnPublish ? (
            <Box className="pl-0 pb-2">
              <TagPicker
                label="Auto-tag name"
                placeholder="Search or create tag..."
                tags={(tagsQuery.data?.data ?? []) as TagOption[]}
                selectedTags={selectedAutoTag}
                onChange={(tags) => {
                  const next = tags.slice(-1);
                  setSelectedAutoTag(next);
                  setSettingsDraft((previous) =>
                    previous
                      ? { ...previous, autoTagName: next[0]?.name ?? '' }
                      : previous
                  );
                }}
                onCreateTag={
                  isAdmin
                    ? async (name, color) => {
                        const result = await createTagMutation.mutateAsync({ name, color });
                        return result.tag;
                      }
                    : undefined
                }
                isDisabled={!isAdmin || updateMutation.isPending}
                isLoading={tagsQuery.isLoading || createTagMutation.isPending}
                description="Choose one tag. Create a new one inline if needed."
              />
            </Box>
          ) : null}

          <SettingsRow
            title="Auto-maintain"
            description="Turn on to let Cofactor's maintenance agent fix flaky and outdated flows without asking. It also pins every flow's cache org-wide, so you don't need the pin setting below. It may run flows live against your application (using the flows' credentials), edit flows, file issues, and update your product map. Every change is recorded in the flow's Activity."
            onPress={
              isAdmin && !updateMutation.isPending
                ? () =>
                    setSettingsDraft((previous) =>
                      previous
                        ? { ...previous, autoMaintainEnabled: !previous.autoMaintainEnabled }
                        : previous
                    )
                : undefined
            }
          >
            <Switch
              isDisabled={!isAdmin || updateMutation.isPending}
              isSelected={settingsDraft.autoMaintainEnabled}
              onChange={(isSelected) =>
                setSettingsDraft((previous) =>
                  previous ? { ...previous, autoMaintainEnabled: isSelected } : previous
                )
              }
            />
          </SettingsRow>

          <SettingsRow
            title="Pin converged flows (without Auto-maintain)"
            description={
              settingsDraft.autoMaintainEnabled
                ? "Pins each flow's cache once a run replays every cached step deterministically from verified rows; it does not edit flows or file issues. Not needed while Auto-maintain is on, which already pins every flow org-wide."
                : "Pins each flow's cache once a run replays every cached step deterministically from verified rows, so future runs keep reading it instead of drifting back to the agent. It does not edit flows or file issues (if cached steps stop replaying it only re-records the cache); choose Auto-maintain above for that."
            }
            onPress={
              isAdmin && !updateMutation.isPending && !settingsDraft.autoMaintainEnabled
                ? () =>
                    setSettingsDraft((previous) =>
                      previous
                        ? { ...previous, cachePinByDefault: !previous.cachePinByDefault }
                        : previous
                    )
                : undefined
            }
          >
            <Switch
              isDisabled={!isAdmin || updateMutation.isPending || settingsDraft.autoMaintainEnabled}
              isSelected={settingsDraft.cachePinByDefault}
              onChange={(isSelected) =>
                setSettingsDraft((previous) =>
                  previous ? { ...previous, cachePinByDefault: isSelected } : previous
                )
              }
            />
          </SettingsRow>

          {showExternalParallelism ? (
            <NumberField
              label="Max parallel external-suite sandboxes"
              value={settingsDraft.maxParallelExternalTests}
              onChange={(value) =>
                setSettingsDraft((previous) =>
                  previous && value !== null
                    ? { ...previous, maxParallelExternalTests: Math.round(value) }
                    : previous
                )
              }
              minValue={1}
              maxValue={1000}
              showStepper
              isDisabled={!isAdmin || updateMutation.isPending}
              className="max-w-xs"
              description="Your organization's total sandbox limit, shared by all external test runs at the same time. Higher is faster but uses more capacity."
            />
          ) : null}

          {!isAdmin ? (
            <Text size="sm" className="text-muted-foreground">
              Only organization admins can update workflow settings.
            </Text>
          ) : null}

          <Flex justify="end">
            <Button onPress={saveSettings} isDisabled={!isAdmin || updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Flex>
        </Flex>
      </CardContent>
    </Card>
  );
}
